import { useEffect, useId, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { IconChevronRight } from '@tabler/icons-react';
import '@/styles/member.css';

export type TileTone = 'teal' | 'rose' | 'violet' | 'amber' | 'green' | 'red' | 'orange';

/** A titled group of settings rows on one rounded card (like a phone's Settings app). */
export function SettingsGroup({ title, children }: { title: ReactNode; children: ReactNode }) {
  const id = useId();
  return (
    <section className="rm-group" aria-labelledby={id}>
      <h2 id={id}>{title}</h2>
      <div className="list">{children}</div>
    </section>
  );
}

export interface SettingsRowProps {
  /** Anchor id: /account#documents opens this row and scrolls to it. */
  id: string;
  /** 20 to 22 px Tabler icon, shown in a coloured tile. */
  icon: ReactNode;
  tone?: TileTone;
  title: ReactNode;
  /** One muted line under the title (a value or a hint). */
  summary?: ReactNode;
  /** Right-hand status (a pill or a short value). */
  trailing?: ReactNode;
  /** What opens under the row. */
  children?: ReactNode;
  /** The panel is always shown (no expand), e.g. Appearance. */
  alwaysOpen?: boolean;
  /** Panel uses the full row width instead of lining up with the title. */
  wide?: boolean;
}

/**
 * One settings row: 64 px tall, icon tile, title, summary, status and a chevron. Tapping it opens
 * the section in place (no new page), so everything stays on Account and deep links still work.
 */
export function SettingsRow({ id, icon, tone = 'teal', title, summary, trailing, children, alwaysOpen, wide }: SettingsRowProps) {
  const location = useLocation();
  const panelId = useId();
  const [open, setOpen] = useState(() => alwaysOpen || location.hash === `#${id}`);

  useEffect(() => {
    if (location.hash === `#${id}`) setOpen(true);
  }, [location.hash, id]);

  const tile = <span className={`tile ${tone === 'teal' ? '' : tone}`} aria-hidden="true">{icon}</span>;
  const text = (
    <span className="txt">
      <b>{title}</b>
      {summary && <span>{summary}</span>}
    </span>
  );

  if (alwaysOpen) {
    return (
      <div className="rm-srow static" id={id}>
        <div className="head">{tile}{text}{trailing && <span className="trail">{trailing}</span>}</div>
        <div className={`panel${wide ? ' wide' : ''}`}>{children}</div>
      </div>
    );
  }

  return (
    <div className="rm-srow" id={id}>
      <button type="button" className="rowbtn" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((o) => !o)}>
        {tile}
        {text}
        {trailing && <span className="trail">{trailing}</span>}
        <span className="chev" aria-hidden="true"><IconChevronRight size={20} /></span>
      </button>
      <div id={panelId} className={`panel${wide ? ' wide' : ''}`} hidden={!open}>
        {open && children}
      </div>
    </div>
  );
}
